import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { API_ENDPOINTS } from '../config/constants';
import './CRMAtendimentoConfigModal.css';
import AppIcon from './icons/AppIcon';

/**
 * Modal de configuração específico para a lista "Em Atendimento"
 * Contém apenas a ferramenta de Follow-up Automático
 */
export default function CRMAtendimentoConfigModal({ list, onClose, onSave, onDelete, projectId }) {
  const [loading, setLoading] = useState(false);
  const [connection, setConnection] = useState(null);

  // Follow-up Rules States
  const [followUpRules, setFollowUpRules] = useState([]);
  const [projectLists, setProjectLists] = useState([]);
  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [newRule, setNewRule] = useState({ targetListId: '', delayHours: 24 });
  const [loadingRules, setLoadingRules] = useState(false);
  const [followUpEnabled, setFollowUpEnabled] = useState(false);

  // ✅ Carregar conexão do projeto
  useEffect(() => {
    const loadConnection = async () => {
      try {
        const { data: project, error: projectError } = await supabase
          .from('projects')
          .select('settings, project_type')
          .eq('id', projectId)
          .single();

        if (projectError) throw projectError;

        // Buscar connection_id de crm ou crm_multi
        let connectionId = null;
        if (project?.project_type === 'crm_multi') {
          const connectionIds = project?.settings?.crm_multi?.connection_ids || [];
          connectionId = connectionIds[0] || null; // Usa a primeira conexão
        } else {
          connectionId = project?.settings?.crm?.connection_id;
        }

        if (connectionId) {
          const { data: conn, error: connError } = await supabase
            .from('connections')
            .select('id, name, number')
            .eq('id', connectionId)
            .single();

          if (connError) throw connError;
          setConnection(conn);
        }
      } catch (error) {
        console.error('Erro ao carregar conexão:', error);
      }
    };

    if (projectId) {
      loadConnection();
    }
  }, [projectId]);

  // ✅ Carregar listas do projeto para o dropdown de destino
  useEffect(() => {
    const loadProjectLists = async () => {
      try {
        const { data: lists, error } = await supabase
          .from('project_lists')
          .select('id, name')
          .eq('project_id', projectId)
          .order('name', { ascending: true });

        if (error) throw error;
        setProjectLists(lists || []);
      } catch (error) {
        console.error('Erro ao carregar listas do projeto:', error);
      }
    };

    if (projectId) {
      loadProjectLists();
    }
  }, [projectId]);

  // ✅ Carregar regras de follow-up da lista
  useEffect(() => {
    const loadFollowUpRules = async () => {
      if (!list?.id) return;
      
      try {
        setLoadingRules(true);
        const { data: rules, error } = await supabase
          .from('crm_followup_rules')
          .select('*')
          .eq('origin_list_id', list.id)
          .order('order_index', { ascending: true });

        if (error) throw error;
        
        setFollowUpRules(rules || []);
        const hasActiveRules = rules && rules.some(r => r.active);
        setFollowUpEnabled(hasActiveRules || false);
      } catch (error) {
        console.error('Erro ao carregar regras de follow-up:', error);
      } finally {
        setLoadingRules(false);
      }
    };

    loadFollowUpRules();
  }, [list?.id]);

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Salvar configurações da lista (se necessário no futuro)
      alert('Configurações salvas com sucesso!');
      onSave(list);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      alert('Erro ao salvar: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Criar nova regra de follow-up
  const handleCreateFollowUpRule = async () => {
    try {
      if (!newRule.targetListId || !newRule.delayHours) {
        alert('Por favor, preencha todos os campos');
        return;
      }

      const targetList = projectLists.find(l => l.id === newRule.targetListId);
      if (!targetList) {
        alert('Lista de destino não encontrada');
        return;
      }

      setLoadingRules(true);

      // Obter token de forma correta
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('Sessão expirada. Por favor, faça login novamente');
      }

      const url = API_ENDPOINTS.CRM_FOLLOWUP_RULES;
      
      const payload = {
        projectId,
        originListId: list.id,
        originListName: list.title,
        targetListId: newRule.targetListId,
        targetListName: targetList.name,
        delayHours: parseInt(newRule.delayHours)
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify(payload)
      });

      let responseData;
      try {
        responseData = await response.json();
      } catch (parseError) {
        throw new Error(`Erro ${response.status}: Resposta inválida do servidor`);
      }

      if (!response.ok) {
        throw new Error(responseData?.error || `Erro ${response.status}: Erro ao criar regra`);
      }

      const rule = responseData.data;
      if (!rule) {
        throw new Error('Regra não foi retornada pelo servidor');
      }
      
      setFollowUpRules([...followUpRules, rule]);
      setNewRule({ targetListId: '', delayHours: 24 });
      setShowFollowUpForm(false);
      alert('Regra de follow-up criada com sucesso!');
    } catch (error) {
      console.error('Erro ao criar regra:', error);
      alert('Erro ao criar regra: ' + error.message);
    } finally {
      setLoadingRules(false);
    }
  };

  // ✅ Deletar regra de follow-up
  const handleDeleteFollowUpRule = async (ruleId) => {
    if (!window.confirm('Tem certeza que deseja deletar esta regra?')) return;

    try {
      setLoadingRules(true);

      const response = await fetch(`${API_ENDPOINTS.CRM_FOLLOWUP_RULES}/${ruleId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ projectId })
      });

      if (!response.ok) {
        throw new Error('Erro ao deletar regra');
      }

      setFollowUpRules(followUpRules.filter(r => r.id !== ruleId));
      alert('Regra deletada com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar regra:', error);
      alert('Erro ao deletar regra: ' + error.message);
    } finally {
      setLoadingRules(false);
    }
  };

  // ✅ Ativar/Desativar regra de follow-up
  const handleToggleFollowUpRule = async (rule) => {
    try {
      setLoadingRules(true);

      const response = await fetch(`${API_ENDPOINTS.CRM_FOLLOWUP_RULES}/${rule.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          projectId,
          active: !rule.active
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao atualizar regra');
      }

      const { data: updatedRule } = await response.json();
      setFollowUpRules(followUpRules.map(r => r.id === rule.id ? updatedRule : r));
    } catch (error) {
      console.error('Erro ao atualizar regra:', error);
      alert('Erro ao atualizar regra: ' + error.message);
    } finally {
      setLoadingRules(false);
    }
  };

  return (
    <div className="modal-overlay-crm-atendimento" onClick={onClose}>
      <div className="crm-atendimento-config-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="crm-modal-header">
          <div className="crm-modal-title-section">
            <AppIcon name="support_agent" />
            <div>
              <h2>Configurar</h2>
              <p className="list-name">{list?.title}</p>
            </div>
          </div>
          <button className="btn-close-modal" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="crm-modal-content">
          {/* Seção Principal */}
          <div className="crm-modal-main">
            {/* ✅ Seção de Follow-up Automático */}
            <div className="crm-section">
              <div className="section-header">
                <div className="header-left">
                  <AppIcon name="schedule" />
                  <h4>Follow-up Automático</h4>
                </div>
              </div>

              {/* ✅ Toggle de Habilitação */}
              <div className="tool-enable-section">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={followUpEnabled}
                    onChange={(e) => setFollowUpEnabled(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-text">
                    {followUpEnabled 
                      ? '✅ Follow-up ativo' 
                      : '❌ Follow-up inativo'}
                  </span>
                </label>
                <small className="toggle-description">
                  {followUpEnabled 
                    ? 'Leads serão movidos automaticamente conforme as regras configuradas'
                    : 'Nenhum lead será movido automaticamente'}
                </small>
              </div>

              {/* Lista de Regras Existentes */}
              {followUpRules.length > 0 && (
                <div className="followup-rules-container">
                  <div className="followup-rules-label">Regras Configuradas ({followUpRules.length}):</div>
                  <div className="followup-rules-list">
                    {followUpRules.map((rule, index) => (
                      <div key={rule.id} className="followup-rule-item">
                        <div className="rule-info">
                          <div className="rule-number">Follow-up {index + 1}</div>
                          <div className="rule-details">
                            <span className="rule-arrow">→</span>
                            <span className="rule-target">{rule.target_list_name}</span>
                            <span className="rule-delay">após {rule.delay_hours}h</span>
                          </div>
                        </div>
                        <div className="rule-actions">
                          <button
                            className={`btn-toggle-rule ${rule.active ? 'active' : 'inactive'}`}
                            onClick={() => handleToggleFollowUpRule(rule)}
                            disabled={loadingRules}
                            title={rule.active ? 'Desativar' : 'Ativar'}
                          >
                            <AppIcon name={rule.active ? 'check_circle' : 'radio_button_unchecked'} />
                          </button>
                          <button
                            className="btn-delete-rule"
                            onClick={() => handleDeleteFollowUpRule(rule.id)}
                            disabled={loadingRules}
                            title="Deletar"
                          >
                            <AppIcon name="delete" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Formulário para Criar Nova Regra */}
              {!showFollowUpForm ? (
                <button
                  className="btn-add-followup-rule"
                  onClick={() => setShowFollowUpForm(true)}
                  disabled={loadingRules}
                >
                  <AppIcon name="add" />
                  Adicionar Regra de Follow-up
                </button>
              ) : (
                <div className="followup-form">
                  <div className="form-group">
                    <label>Quantas horas para encaminhar?</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="Ex: 1, 2, 24, 48"
                      value={newRule.delayHours}
                      onChange={(e) => setNewRule({ ...newRule, delayHours: e.target.value })}
                      disabled={loadingRules}
                      className="delay-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Encaminhar para</label>
                    <select
                      value={newRule.targetListId}
                      onChange={(e) => setNewRule({ ...newRule, targetListId: e.target.value })}
                      disabled={loadingRules}
                    >
                      <option value="">Selecione uma lista...</option>
                      {projectLists
                        .filter(l => l.id !== list.id)
                        .map(l => (
                          <option key={l.id} value={l.id}>
                            {l.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="form-actions">
                    <button
                      className="btn-cancel-form"
                      onClick={() => setShowFollowUpForm(false)}
                      disabled={loadingRules}
                    >
                      Cancelar
                    </button>
                    <button
                      className="btn-create-rule"
                      onClick={handleCreateFollowUpRule}
                      disabled={loadingRules || !newRule.targetListId || !newRule.delayHours}
                    >
                      {loadingRules ? 'Salvando...' : 'Salvar Configuração'}
                    </button>
                  </div>
                </div>
              )}

              <small className="followup-description">
                Quando habilitado, leads que permanecerem nesta lista por mais de N horas serão automaticamente movidos para a lista de destino.
              </small>
            </div>

            {/* ✅ Informações sobre a lista */}
            <div className="crm-section info-section">
              <h4>
                <AppIcon name="info" />
                Sobre esta lista
              </h4>
              <p className="info-text">
                A lista <strong>"Em Atendimento"</strong> é destinada aos leads que estão sendo atendidos ativamente.
                Use o follow-up automático para mover leads inativos para outras listas após um período de tempo.
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="crm-modal-sidebar">
            <h4>Informações</h4>
            
            {/* ✅ Informações da conexão */}
            {connection && (
              <div className="info-box-sidebar">
                <div className="info-item">
                  <AppIcon name="phone" />
                  <div>
                    <strong>Conexão</strong>
                    <p>{connection.name}</p>
                    <p style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>{connection.number}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="info-box-sidebar">
              <div className="info-item">
                <AppIcon name="credit_card" />
                <div>
                  <strong>Cards</strong>
                  <p>{list?.cards?.length || 0} itens</p>
                </div>
              </div>
              
              <div className="info-item">
                <AppIcon name="schedule" />
                <div>
                  <strong>Criada em</strong>
                  <p>{list?.created_at ? new Date(list.created_at).toLocaleDateString('pt-BR') : 'N/A'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="crm-modal-footer">
          <button className="btn-cancel-crm" onClick={onClose}>
            Cancelar
          </button>
          <button 
            className="btn-save-crm" 
            onClick={handleSave}
            disabled={loading}
          >
            <AppIcon name="check" />
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}
